import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { formatLastActivityDateTime } from '../../util/activityDisplay';

export interface CompanyListCardRow {
  id: string;
  name: string;
  ownerName: string;
  memberNames: string[];
  memberCount: number;
  businessAddress: string;
  dropoffAddress: string;
  dropoffExtraCount: number;
  lastActivityAt: Date | null;
}

interface CompanyListCardProps {
  row: CompanyListCardRow;
  mergePickMode: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onPreview: (id: string) => void;
  onOpenCompanyPage: (id: string, event: React.MouseEvent) => void;
}

const CompanyListCard: React.FC<CompanyListCardProps> = ({
  row,
  mergePickMode,
  isSelected,
  onToggleSelect,
  onPreview,
  onOpenCompanyPage,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      onClick={() => {
        if (mergePickMode) onToggleSelect(row.id);
      }}
      sx={{
        width: '100%',
        p: 1.5,
        borderRadius: 2.5,
        cursor: mergePickMode ? 'pointer' : 'default',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        {mergePickMode ? (
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(row.id)}
            onClick={(event) => event.stopPropagation()}
            sx={{ p: 0.5, mt: 0.25 }}
          />
        ) : null}
        <Avatar
          variant="rounded"
          onClick={(event) => onOpenCompanyPage(row.id, event)}
          sx={{
            width: 40,
            height: 40,
            bgcolor: alpha(accent, 0.16),
            color: accent,
            fontSize: 15,
            fontWeight: 700,
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          {row.name?.charAt(0)?.toUpperCase() || '?'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              noWrap
              onClick={(event) => onOpenCompanyPage(row.id, event)}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {row.name}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {row.ownerName}
          </Typography>
          {row.memberNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 0.75 }}
            >
              {row.memberNames.map((name) => (
                <Chip
                  key={name}
                  size="small"
                  label={name}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    maxWidth: 180,
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.dark,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      px: 0.75,
                    },
                  }}
                />
              ))}
            </Stack>
          ) : null}
          {row.businessAddress ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {row.businessAddress}
            </Typography>
          ) : null}
          {row.dropoffAddress ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                Dropoff: {row.dropoffAddress}
              </Typography>
              {row.dropoffExtraCount > 0 ? (
                <Chip
                  size="small"
                  label={`+${row.dropoffExtraCount}`}
                  sx={{
                    height: 18,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ) : null}
            </Stack>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Last activity: {formatLastActivityDateTime(row.lastActivityAt)}
          </Typography>
          {!mergePickMode ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onPreview(row.id);
              }}
              sx={{
                mt: 1,
                py: 0.5,
                px: 1.25,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1.4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Preview
            </Button>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
};

export default CompanyListCard;
